import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore, safeNextPath } from "@/lib/auth";
import { Plane } from "lucide-react";

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setToken = useAuthStore((s) => s.setToken);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token") || searchParams.get("session_token");
    if (token) {
      setToken(token);
      // § G29 — go where they were HEADED, not to the dashboard. The
      // unconditional /dashboard here silently ate the destination of every
      // protected deep link; the one that mattered was `/claim?token=…`, so an
      // invited attendee who was signed out could never actually claim a trip.
      // `safeNextPath` re-validates: this value came off the URL bar.
      navigate(safeNextPath(searchParams.get("next")) ?? "/dashboard", { replace: true });
    } else {
      const authError = searchParams.get("error");
      if (authError === "no_account") {
        setError("No account found. Please contact the administrator for access.");
      } else {
        setError("No authentication token received. Please try again.");
      }
    }
  }, [searchParams, setToken, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <p className="text-destructive text-lg">{error}</p>
          <a href="/" className="text-primary hover:underline">
            Return to home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <Plane className="h-8 w-8 text-primary animate-pulse mx-auto" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
